import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AdSlot } from '../components/AdSlot';
import { api } from '../api/client';
import { LikeButton } from '../components/LikeButton';
import { ShareSocialBlock } from '../components/ShareSocialBlock';
import { GuestOnboardingBlock } from '../components/GuestOnboardingBlock';
import { useAuth } from '../hooks/useAuth';
import { t, getLocale } from '../hooks/useT';
import { preloadImages } from '../utils/imageCache';

interface ResultState {
  won?: boolean;
  score?: number;
  dezoomsUsed?: number;
  durationSec?: number;
  shareText?: string | null;
  puzzleNumber?: number;
  puzzleId?: string;
  fromRun?: string;
  answer?: string;
  answerEn?: string;
  imageUrl?: string;
  isReplay?: boolean;
  freshnessMultiplier?: number | null;
  isArchive?: boolean;
  zoom0ImageUrl?: string | null;
  neighbors?: ArchiveNeighbors;
}

interface ArchiveNeighbors {
  prevPuzzleNumber: number | null;
  nextPuzzleNumber: number | null;
}

interface RunSummary {
  won: boolean;
  score: number;
  dezoomsUsed: number;
  durationSec: number;
  puzzleNumber: number;
  puzzleId: string;
  isOfficial?: boolean;
  isReplay?: boolean;
  shareText?: string | null;
  likeCount: number;
  liked: boolean;
  answer?: string;
  answerEn?: string;
  imageUrl?: string;
  freshnessMultiplier?: number | null;
  isArchive?: boolean;
  zoom0ImageUrl?: string | null;
  neighbors?: ArchiveNeighbors;
}

const SCORE_DELAY_SEC = 15;

export function ResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as ResultState | null;
  const { user } = useAuth();
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [neighbors, setNeighbors] = useState<ArchiveNeighbors | null>(null);
  const [showScore, setShowScore] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(SCORE_DELAY_SEC);
  const locale = getLocale();
  const needsSummary = !!state?.fromRun && (
    state.dezoomsUsed == null
    || (!state.answer && state.won === false)
    || !state.imageUrl
  );
  const [loading, setLoading] = useState(needsSummary);

  useEffect(() => {
    const runId = state?.fromRun;
    const needsFetch = runId && (
      state?.dezoomsUsed == null
      || (!state?.answer && state?.won === false)
      || !state?.imageUrl
    );
    if (needsFetch) {
      api<RunSummary>(`/runs/${runId}/summary`)
        .then((data) => {
          setSummary(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [state]);

  useEffect(() => {
    if (loading) return;
    if (user?.isPremium) {
      setShowScore(true);
      return;
    }
    setShowScore(false);
    setSecondsLeft(SCORE_DELAY_SEC);
    const tick = window.setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    const done = window.setTimeout(() => {
      setShowScore(true);
      window.clearInterval(tick);
    }, SCORE_DELAY_SEC * 1000);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(done);
    };
  }, [loading, user?.isPremium]);

  const imageUrl = summary?.imageUrl ?? state?.imageUrl;

  useEffect(() => {
    if (imageUrl) preloadImages([imageUrl]);
  }, [imageUrl]);

  const won = summary?.won ?? state?.won ?? false;
  const score = summary?.score ?? state?.score ?? 0;
  const dezoomsUsed = summary?.dezoomsUsed ?? state?.dezoomsUsed ?? 0;
  const durationSec = summary?.durationSec ?? state?.durationSec ?? 0;
  const puzzleNumber = summary?.puzzleNumber ?? state?.puzzleNumber ?? 0;
  const puzzleId = summary?.puzzleId ?? state?.puzzleId;
  const shareText = summary?.shareText ?? state?.shareText ?? null;
  const isOfficial = summary?.isOfficial ?? true;
  const answer = summary?.answer ?? state?.answer;
  const answerEn = summary?.answerEn ?? state?.answerEn;
  const displayAnswer = getLocale() === 'en' && answerEn ? answerEn : answer;
  const isReplay = summary?.isReplay ?? state?.isReplay ?? false;
  const freshnessMultiplier = summary?.freshnessMultiplier ?? state?.freshnessMultiplier ?? null;
  const isArchive = summary?.isArchive ?? state?.isArchive ?? false;
  const zoom0ImageUrl = summary?.zoom0ImageUrl ?? state?.zoom0ImageUrl ?? null;
  const archiveNeighbors = summary?.neighbors ?? state?.neighbors ?? neighbors;

  useEffect(() => {
    if (!isArchive || !puzzleNumber) return;
    if (summary?.neighbors || state?.neighbors) return;
    api<{ neighbors?: ArchiveNeighbors }>(`/archives/${puzzleNumber}`)
      .then((data) => {
        if (data.neighbors) setNeighbors(data.neighbors);
      })
      .catch(() => { /* optional */ });
  }, [isArchive, puzzleNumber, summary?.neighbors, state?.neighbors]);

  const goArchiveNeighbor = (num: number | null) => {
    if (!num) return;
    navigate(`/archives/${num}/play?replay=1`);
  };

  const freshnessLabel = () => {
    if (freshnessMultiplier == null || freshnessMultiplier >= 1) return null;
    if (Math.abs(freshnessMultiplier - 0.5) < 0.01) return t('archives.freshnessHalf');
    return t('archives.freshnessThird');
  };

  if (loading) {
    return <main className="page result"><p className="loading">{t('common.loading')}</p></main>;
  }

  if (!state && !summary) {
    return (
      <main className="page result">
        <div className="result-card">
          <p>Pas de résultat.</p>
          <Link to="/play" className="btn primary">{t('nav.daily')}</Link>
        </div>
      </main>
    );
  }

  const mins = Math.floor(durationSec / 60);
  const secs = durationSec % 60;

  return (
    <main className="page result">
      <div className="result-card">
        {imageUrl && (
          <div className="result-reveal result-reveal--hero">
            <img src={imageUrl} alt="" className="result-reveal-img" />
          </div>
        )}

        {showScore ? (
          <>
            <h1 className={won ? 'win' : 'fail'}>
              {won ? t('result.win') : t('result.fail')}
            </h1>
            {won && !isReplay && <p className="score">{score} pts</p>}
            {won && !isReplay && freshnessLabel() && (
              <p className="guest-hint muted">{freshnessLabel()}</p>
            )}
            {isReplay && (
              <p className="guest-hint muted">{t('result.replayHint')}</p>
            )}
            {!won && displayAnswer && (
              <p className="result-answer">
                {t('result.answer')} <strong>{displayAnswer}</strong>
              </p>
            )}
            {isReplay && !won && (
              <p className="guest-hint muted">{t('result.replayHint')}</p>
            )}
            {!isOfficial && won && score > 0 && !user?.isGuest && (
              <p className="guest-hint muted">Partie invité — connecte-toi pour figurer au classement.</p>
            )}
            <ul className="stats">
              <li><strong>{dezoomsUsed}</strong> dézooms</li>
              <li><strong>{mins}:{secs.toString().padStart(2, '0')}</strong> temps</li>
              <li><strong>#{puzzleNumber}</strong> énigme</li>
            </ul>

            {puzzleId && (
              <div className="result-actions">
                <LikeButton
                  puzzleId={puzzleId}
                  initialCount={summary?.likeCount}
                  initialLiked={summary?.liked}
                />
              </div>
            )}

            {showScore && user?.isGuest && (
              <GuestOnboardingBlock isArchive={isArchive} />
            )}

            <ShareSocialBlock
              puzzleNumber={puzzleNumber}
              isArchive={isArchive}
              zoom0ImageUrl={zoom0ImageUrl}
              locale={locale}
              scoreShareText={shareText}
            />

            {!user?.isPremium && (
              <div className="result-premium-cta">
                <Link to="/premium" className="link-muted">{t('result.premiumLink')}</Link>
              </div>
            )}
            {!user?.isPremium && (
              <AdSlot className="result-ad-slot result-ad-slot--compact" compact />
            )}
          </>
        ) : (
          <div className="result-wait-block">
            <p className="result-ad-wait muted">
              {t('result.waitScore').replace('{seconds}', String(secondsLeft))}
            </p>
            {!user?.isPremium && (
              <div className="result-premium-cta">
                <p className="result-premium-hint muted">{t('result.premiumWaitHint')}</p>
                <Link to="/premium" className="btn secondary">{t('result.premiumCta')}</Link>
              </div>
            )}
          </div>
        )}

        {isArchive && archiveNeighbors && (
          <nav className="archives-result-nav" aria-label={t('archives.title')}>
            <button
              type="button"
              className="btn secondary"
              disabled={!archiveNeighbors.prevPuzzleNumber}
              onClick={() => goArchiveNeighbor(archiveNeighbors.prevPuzzleNumber)}
            >
              ← {t('archives.prev')}
            </button>
            <Link to="/archives" className="btn ghost">
              {t('archives.back')}
            </Link>
            <button
              type="button"
              className="btn secondary"
              disabled={!archiveNeighbors.nextPuzzleNumber}
              onClick={() => goArchiveNeighbor(archiveNeighbors.nextPuzzleNumber)}
            >
              {t('archives.next')} →
            </button>
          </nav>
        )}
      </div>
      <nav className="result-nav">
        <Link to="/leaderboard">{t('nav.leaderboards')}</Link>
        <Link to="/top">{t('nav.top')}</Link>
        <Link to="/">{t('gallery.home')}</Link>
        {!user?.isPremium && showScore && (
          <Link to="/premium">{t('result.premiumLink')}</Link>
        )}
      </nav>
    </main>
  );
}
